<template>
	<section class="contact-prompt">
		<div
			class="contact-prompt__read-only"
			v-if="!editMode"
			data-test="readSection"
		>
			<div class="contact-prompt__read-only-fields">
				<div class="contact-prompt__read-only-item">
					<div class="contact-prompt__read-only-item-label">
						Name
					</div>
					<div class="contact-prompt__read-only-item-value">
						<Ellipsis :data="value.name"></Ellipsis>
					</div>
				</div>
				<div class="contact-prompt__read-only-item">
					<div class="contact-prompt__read-only-item-label">
						Title
					</div>
					<div class="contact-prompt__read-only-item-value">
						<Ellipsis :data="value.title"></Ellipsis>
					</div>
				</div>
				<div class="contact-prompt__read-only-item">
					<div class="contact-prompt__read-only-item-label">
						Email
					</div>
					<div class="contact-prompt__read-only-item-value">
						<Ellipsis :data="value.email"></Ellipsis>
					</div>
				</div>
				<div class="contact-prompt__read-only-item">
					<div class="contact-prompt__read-only-item-label">
						Phone
					</div>
					<div
						class="contact-prompt__read-only-item-value"
						data-test="phoneValue"
					>
						{{ handlingPhoneValue(value.phone) }}
					</div>
				</div>
			</div>
			<div
				class="contact-prompt__read-only-actions"
				v-if="hasUpdatePermission"
			>
				<ButtonGroup>
					<template slot="container">
						<Button
							labelText="Edit"
							:btnClick="editContact"
							className="btn--has-icon"
							:iconBeforeLabel="true"
							iconClassName="icon-edit"
						/>
						<Button
							ref="tooltipPositionRef"
							:btnClick="deleteContact"
							:className="'btn--icon-only'"
							:iconBeforeLabel="false"
							:iconClassName="'icon-delete-glenn'"
							v-if="hasDeletePermission"
							data-test="deleteBtn"
						/>
					</template>
				</ButtonGroup>
			</div>
		</div>
		<div class="contact-prompt__edit" v-else data-test="editSection">
			<div class="contact-prompt__edit-details">
				<div class="contact-prompt__edit-heading">
					<div class="contact-prompt__edit-text">Contact Details</div>
				</div>
				<div class="contact-prompt__edit-fields">
					<Input
						class="contact-prompt__edit-name"
						className="field__input"
						label="Name"
						name="contactName"
						:required="true"
						v-model="dataValue.name"
						:forceCustomErrorMessage="nameCustomErrorMessage"
						@input="setName"
					/>
					<Input
						class="contact-prompt__edit-title"
						className="field__input"
						label="Title"
						name="contactTitle"
						:required="false"
						v-model="dataValue.title"
						:forceCustomErrorMessage="titleCustomErrorMessage"
						@input="setTitle"
					/>
				</div>
			</div>
			<div class="contact-prompt__edit-method">
				<div class="contact-prompt__edit-heading">
					<div class="contact-prompt__edit-text">
						Contact Method *
					</div>
					<div class="contact-prompt__edit-text-help">
						Only one required
					</div>
				</div>

				<div class="contact-prompt__edit-fields">
					<Input
						className="field__input"
						label="Email"
						name="email"
						inputType="Email"
						placeholder="example@mail.com"
						v-model="dataValue.email"
						:maxLength="200"
						:forceCustomErrorMessage="emailCustomErrorMessage"
						@input="setEmail"
					/>
					<Input
						className="field__input"
						label="Phone"
						name="phone"
						inputType="Number"
						placeholder="(123) 456-7890"
						v-model="dataValue.phone"
						:forceCustomErrorMessage="phoneCustomErrorMessage"
						@input="setPhoneNumber"
						:minLength="10"
						:maxLength="11"
					/>
				</div>
			</div>

			<div class="contact-prompt__edit-actions">
				<ButtonGroup data-test="saveCancelButton">
					<template slot="container">
						<Button
							className="btn--primary"
							labelText="Save Contact"
							:btnClick="save"
							:disabled="
								$v.$invalid || !atLeastEmailOrPhoneRequired
							"
						/>
						<Button
							className="btn btn--link"
							labelText="Cancel"
							:btnClick="cancel"
						/>
					</template>
				</ButtonGroup>
			</div>
		</div>
		<Tooltip
			:display="displayTooltip"
			:offset="[0, 12]"
			variation="warning"
			trackedRefName="tooltipPositionRef"
			text="At least one Support Contact is Required.  Please choose edit to update the Support Contact."
			position="top"
		>
		</Tooltip>
	</section>
</template>

<script>
import Button from '@/components/forms/button/button';
import Tooltip from '@/components/shells/tooltip/tooltip';
import Input from '@/components/forms/input/input';
import ButtonGroup from '@/components/forms/button/button-group';
import Ellipsis from '@/components/visual/ellipsis';
import {
	required,
	email,
	numeric,
	maxLength,
	minLength
} from 'vuelidate/lib/validators';

export default {
	name: 'VendorContactFlyoutItem',
	components: {
		Button,
		Tooltip,
		Input,
		ButtonGroup,
		Ellipsis
	},
	props: {
		value: {
			type: Object
		},
		editMode: {
			type: Boolean,
			default: false
		},
		disabledDelete: {
			type: Boolean,
			default: false
		}
	},
	validations: {
		dataValue: {
			name: {
				required,
				minLength: minLength(3),
				maxLength: maxLength(100)
			},
			title: {
				minLength: minLength(3),
				maxLength: maxLength(200)
			},
			email: {
				email
			},
			phone: {
				minLength: minLength(10),
				maxLength: maxLength(11),
				numeric
			}
		}
	},
	data() {
		return {
			dataValue: {},
			displayTooltip: false
		};
	},
	mounted() {
		document.addEventListener('click', this.hideTooltip);
	},
	beforeDestroy() {
		document.removeEventListener('click', this.hideTooltip);
	},
	computed: {
		nameCustomErrorMessage() {
			if (this.$v.dataValue.name.$dirty) {
				if (!this.$v.dataValue.name.required) {
					return 'Name is required';
				} else if (!this.$v.dataValue.name.minLength) {
					return `Name must have at least ${this.$v.dataValue.name.$params.minLength.min} letters`;
				} else if (!this.$v.dataValue.name.maxLength) {
					return `Name must have less than ${this.$v.dataValue.name.$params.maxLength.max} letters`;
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		titleCustomErrorMessage() {
			if (this.$v.dataValue.title.$dirty) {
				if (!this.$v.dataValue.title.minLength) {
					return `Title must have at least ${this.$v.dataValue.title.$params.minLength.min} letters`;
				} else if (!this.$v.dataValue.title.maxLength) {
					return `Title must have less than ${this.$v.dataValue.title.$params.maxLength.max} letters`;
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		emailCustomErrorMessage() {
			if (this.$v.dataValue.email.$dirty) {
				if (!this.$v.dataValue.email.email) {
					return 'Email format invalid';
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		phoneCustomErrorMessage() {
			if (this.$v.dataValue.phone.$dirty) {
				if (
					!this.$v.dataValue.phone.minLength ||
					!this.$v.dataValue.phone.maxLength
				) {
					return `Phone Number must include between ${this.$v.dataValue.phone.$params.minLength.min} and ${this.$v.dataValue.phone.$params.maxLength.max} numbers`;
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		atLeastEmailOrPhoneRequired() {
			if (
				(!this.emailCustomErrorMessage &&
					this.$v.dataValue.email.$model) ||
				(!this.phoneCustomErrorMessage &&
					this.$v.dataValue.phone.$model)
			) {
				return true;
			}
			return false;
		},
		hasUpdatePermission() {
			return this.validatePermission('vendors.update');
		},
		hasDeletePermission() {
			return this.validatePermission('vendors.create');
		},
		hasAtleastOneContact() {
			const { email, phone } = this.dataValue;
			return !!(
				(email && !this.invalidEmail) ||
				(phone && !this.invalidEmail)
			);
		}
	},
	methods: {
		setName(value) {
			this.dataValue.name = value;
			this.$v.dataValue.name.$touch();
			this.$emit('input', this.dataValue);
		},
		setEmail(value) {
			this.dataValue.email = value;
			this.$v.dataValue.email.$touch();
			this.$emit('input', this.dataValue);
		},
		setTitle(value) {
			this.dataValue.title = value;
			this.$v.dataValue.title.$touch();
			this.$emit('input', this.dataValue);
		},
		setPhoneNumber(value) {
			this.dataValue.phone = value;
			this.$v.dataValue.phone.$touch();
			this.$emit('input', this.dataValue);
		},
		editContact() {
			this.$emit('edit');
		},
		deleteContact() {
			if (this.disabledDelete) {
				this.displayTooltip = !this.displayTooltip;
			} else {
				this.$emit('delete');
			}
		},
		save() {
			this.$emit('save');
		},
		cancel() {
			this.$emit('cancel');
		},
		handlingPhoneValue(value) {
			if (!value) {
				return 'N/A';
			}
			if (value.length === 10) {
				return (
					'(' +
					value.substring(0, 3) +
					')-' +
					value.substring(3, 6) +
					'-' +
					value.substring(6, value.length)
				);
			} else {
				return (
					'+' +
					value.substring(0, 1) +
					'(' +
					value.substring(1, 4) +
					')-' +
					value.substring(4, 7) +
					'-' +
					value.substring(7, value.length)
				);
			}
		},
		hideTooltip() {
			this.displayTooltip = false;
		}
	},
	watch: {
		value: {
			deep: true,
			immediate: true,
			handler(obj) {
				this.dataValue = Object.assign({}, obj);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.contact-prompt {
	&__read-only {
		display: grid;
		grid-template-columns: 1fr;

		@include mq($from: lg) {
			grid-template-columns: 1fr auto;
		}
	}

	&__read-only-fields {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: $gutter-xl;

		@include mq($from: lg) {
			grid-template-columns: 1fr 1fr 1fr 1fr;
		}
	}

	&__read-only-item {
		overflow: hidden;

		&-label {
			@include body-text('regular-emphasis');
		}

		&-value {
			@include body-text('regular');
			margin-top: $gutter-md;
		}
	}

	&__read-only-actions {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		margin: $gutter-xxl 0 0 0;
		gap: $gutter-xl;

		@include mq($from: lg) {
			margin: 0 0 0 $gutter-xxl;
		}
	}

	&__edit {
		display: grid;
		grid-template-columns: 1fr;
		gap: $gutter-xl;

		@include mq($from: lg) {
			grid-template-columns: 1fr 1fr;
		}
	}

	&__edit-heading {
		@include mq($from: lg) {
			height: 41px;
		}
	}

	&__edit-text {
		@include heading('md');
	}

	&__edit-text-help {
		@include body-text('sm-supporting');
		line-height: 14px;
		margin-top: $gutter-xs;
	}

	&__edit-fields {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: $gutter-xl;
		margin-top: $gutter-md;
	}

	&__edit-actions {
		display: flex;
		justify-content: flex-end;

		@include mq($from: lg) {
			grid-column: 1 / 3;
		}
	}
}

::v-deep .btn-set {
	align-items: center;

	> * {
		&:not(:last-child) {
			margin-right: $gutter-xl;
		}
	}
	.btn--icon-only {
		font-size: 24px;
	}
}
</style>
